import {httpClient} from '@/api/baseApi';
import {SpectrumEventType, SpectrumYAxisType} from '@/enums/diagrams';
import {DateTimeInterval} from '@/utils/dateTimeItervalUtils';

/*
    Метод преобразования частоты спектра (TODO: вынести на сервер)
*/
function getSpectrumFreqFormatted(
  index: number,
  leftX: number,
  rightX: number,
  dataLength: number,
) {
  return Math.round((leftX + (index * (rightX - leftX)) / (dataLength - 1)) * 100) / 100;
}

/*
    Общая функция получения сигнала для всех типов ПМ
*/
export async function getSignal(
  routeName: string,
  params: {id: number; dateInterval: DateTimeInterval; fieldAlias: string; statType?: string},
) {
  const parameters: any = {
    start_date: params.dateInterval.startDateTime,
    end_date: params.dateInterval.endDateTime,
    field_alias: params.fieldAlias,
    points_to_keep: 1000,
  };

  // TODO
  if (params.statType?.trim()) parameters.stat_type = params.statType.replaceAll('_', '-');

  const {data} = await httpClient.get(`${routeName}/${params.id}/signal/`, {
    params: parameters,
  });

  if (!data[0].length) return {dataSet: []};

  const dataSet: {x: number; y: number}[] = data[0].map((elem: number[]): {
    x: number;
    y: number;
  } => ({
    x: elem[0],
    y: elem[1],
  }));
  return {dataSet};
}

export async function getRawData(
  routeName: string,
  params: {id: number; fieldAlias: string; date?: number; offset?: number; length?: number},
) {
  const parameters: any = {
    field_alias: params.fieldAlias,
    date: params.date,
    offset: params.offset,
    length: params.length,
    points_to_keep: 1000,
  };

  const {data} = await httpClient.get(`${routeName}/${params.id}/raw/`, {
    params: parameters,
  });

  const dataLength: number = data[0][0][1];
  const left = data[0][0][0][0][0];
  const right = data[0][0][0][data[0][0][0].length - 1][0];

  const dataSet: {
    timestamp: number;
    leftX: number;
    rightX: number;
    dataLength: number;
    spectrum: {customDataIndex: number; x: number; y: number}[];
  } = {
    timestamp: data[0][0][2],
    leftX: left,
    rightX: right,
    dataLength,
    spectrum: data[0][0][0].map((e: any) => ({
      customDataIndex: e[1],
      x: e[1],
      y: e[0],
    })),
  };
  return dataSet;
}

/*
    Рассчитать параметр length для обрезки спектра на нужной частоте:
    1600 Гц — для Low и Energy, 800 Hz — для Envelope
*/
async function getTruncatedLength(
  routeName: string,
  param_id: number,
  params: {
    event_type: number;
    date?: number;
    spectrum_value_type: string;
    field_alias?: string;
    offset?: number;
    length?: number;
    central_frequency?: number;
    bandwidth?: number;
  },
) {
  params.offset = 0;
  params.length = 1;
  const {data} = await httpClient.get(`${routeName}/${param_id}/spectrum/`, {params});
  // Частоты обрезки для разных типов спектров:
  const cutoff_freq = [1600, 800, 1600];
  if (data[0][0] && data[0][0][0].length == 1) {
    return Math.floor((cutoff_freq[params.event_type] * data[0][0][1]) / data[0][0][4]);
  }
  return 0;
}

/*
    Общая функция получения спектра для всех типов ПМ
*/
export async function getSpectrum(
  routeName: string,
  parameters: {
    id: number;
    spectrumType: SpectrumEventType;
    date?: number;
    offset?: number;
    length?: number;
    spectrumValueType?: SpectrumYAxisType;
    fieldAlias?: string; //Для совмещенных спектров (в dtcp акселерометре) нужно указывать поле по которому они хранятся
    central_frequency?: number;
    bandwidth?: number;
  },
) {
  const params: any = {};
  // != вместо !== нужно, чтобы отсечь undefined
  if (parameters.offset != null) params.offset = parameters.offset;
  if (parameters.length != null) params.length = parameters.length;
  if (parameters.date != null) params.date = parameters.date;
  if (parameters.spectrumValueType != null)
    params.spectrum_value_type = parameters.spectrumValueType;

  if (parameters.fieldAlias) params.field_alias = parameters.fieldAlias;
  if (parameters.central_frequency) params.central_frequency = parameters.central_frequency;
  if (parameters.bandwidth) params.bandwidth = parameters.bandwidth;

  params.event_type = parameters.spectrumType as number;

  if (params.offset == null && parameters.length == null) {
    const truncated_length: number = await getTruncatedLength(routeName, parameters.id, params);
    params.offset = 0;
    params.length = truncated_length;
  }

  params.points_to_keep = 1000;

  const {data} = await httpClient.get(`${routeName}/${parameters.id}/spectrum/`, {params});

  if (data[0][0]) {
    const leftX = data[0][0][3];
    const rightX = data[0][0][4];
    const dataLength: number = data[0][0][1];

    const dataSet: {
      timestamp: number;
      leftX: number;
      rightX: number;
      dataLength: number;
      spectrum: {customDataIndex: number; x: number; y: number}[];
    } = {
      timestamp: data[0][0][2],
      leftX,
      rightX,
      dataLength,
      spectrum: data[0][0][0].map((e: any) => ({
        customDataIndex: e[1] - 1, //нельзя называть поле index, и другими, которые могут встретиться в Highchart
        x: getSpectrumFreqFormatted(e[1] - 1, leftX, rightX, dataLength),
        y: e[0],
      })),
    };
    return dataSet;
  }

  return {
    leftX: 0,
    rightX: 0,
    dataLength: 0,
    timestamp: parameters.date,
    spectrum: [],
  };
}

/*
  Общая функция получения типов событий (из базы) модуля
*/
export async function getEventTypes(routeName: string) {
  const {data} = await httpClient.get(`${routeName}/events/event_types`);
  return data;
}

/*
  Общая функция получения списка последних событий
*/
export async function getLastEvents(
  routeName: string,
  parameters: {
    id: number;
    eventType: string;
    limit?: number;
    startDate?: number;
    endDate?: number;
  },
) {
  const params: any = {event_type: parameters.eventType};

  if (parameters.limit) params.limit = parameters.limit;
  if (parameters.startDate) params.start_date = parameters.startDate;
  if (parameters.endDate) params.end_date = parameters.endDate;

  const {data} = await httpClient.get(`${routeName}/${parameters.id}/events/last`, {params});

  //TODO: код для форматирования результата
  return data;
}

/*
  Общая функция получения данных события
*/
export async function getEventData(
  routeName: string,
  parameters: {
    id: number;
    eventType: string;
    timestamp: number;
  },
) {
  const params: any = {
    event_type: parameters.eventType,
    timestamp: parameters.timestamp,
  };
  const {data} = await httpClient.get(`${routeName}/${parameters.id}/events/data`, {params});
  return data;
}
